import React from "react";
import * as Yup from 'yup';
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
import MUIAutocomplete from '@mui/material/Autocomplete';
import MUITextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import InfoIconWithTooltip from "components/InfoIconWithTooltip";
import ActionButtons from 'components/ActionButtons';
import InprepLogoEditor from "./InprepLogoEditor";
import { overwriteClientById } from "services";
import { useSnackbar } from "contexts";
import { useFormik } from "formik";
import { Session } from "utils";
import TextField from "components/CustomFields";

const useStyle = makeStyles(theme => ({
  controlContainer: {
    width: '50%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  buttonContainer: {
    width: '50%',
    display: 'flex',
    justifyContent: 'flex-start'
  },
  textField: {
    width: '50%', minWidth: 380
  },
}));

export function Label({ value, tooltip }) {
  return (
    <Box display='flex' gap={1} alignItems='center' mb={2}>
      <Typography variant="body01-bold">
        {value}
      </Typography>
      {tooltip && <InfoIconWithTooltip title={tooltip} placement="right" />}
    </Box>
  );
}

const Tooltips = {
    redirectUrl: 'Upon clicking logo, user will be redirected to the following URL',
    customizeUrl: 'Onboarding Url for User Portal',
    headerLogo: "Logo which will be displayed to students in the Header",
    enabledFeatures: "Select which pages should be visible on the user dashboard. By default, all pages are enabled.",
};

const FEATURE_OPTIONS = [
    { _id: 'nhss', name: 'Shortlisting', label: 'Shortlisting' },
    { _id: 'llgl', name: 'Blogs', label: 'Blogs' },
    { _id: 'vewI', name: 'Interview Wizard', label: 'Interview Wizard' }
];

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function General() {
  const classes = useStyle();
  const snackbar = useSnackbar();

  const [loading, setLoading] = React.useState(false);
  const [edit, setEdit] = React.useState(false);

    const {
        values,
        errors,
        handleSubmit,
        handleChange,
        setFieldValue
    } = useFormik({
        initialValues: {
            redirectUrl: Session?.user?.metadata?.redirectUrl || '',
            customizeUrl: process.env.REACT_APP_INPREP_URL + '/' + (Session?.user?.name || '').toLowerCase().split(' ').join('-'),
            enabledFeatures: Session?.user?.metadata?.enabledFeatures !== undefined
                ? (Session.user.metadata.enabledFeatures.length > 0
                    ? FEATURE_OPTIONS.filter(option =>
                        Session.user.metadata.enabledFeatures.includes(option._id)
                      )
                    : [])
                : FEATURE_OPTIONS,
        },
        validationSchema: new Yup.ObjectSchema({
            redirectUrl: Yup.string().url('Must be a valid url').trim().required('Required'),
            customizeUrl: Yup.string().trim(),
            enabledFeatures: Yup.array(),
        }),
        onSubmit: async (values) => {
          try {
              setLoading(true);

              console.log('values.enabledFeatures:', values.enabledFeatures);

              // Send only array of IDs
              const enabledFeaturesPayload = values.enabledFeatures
                .filter(feature => feature && feature._id)
                .map(feature => feature._id);

              console.log('Payload being sent:', enabledFeaturesPayload);

              const client = {
                metadata: {
                  redirectUrl: values.redirectUrl,
                  enabledFeatures: enabledFeaturesPayload
                }
              };

        const newClient = await overwriteClientById(Session.userId, client);

        Session._setUser(newClient);
        setEdit(false);

        snackbar.showSnackbar('Successfully saved', 'success');
      } catch (error) {
        snackbar.showSnackbar(error.message, 'error');
      } finally {
        setLoading(false);
      }
    },
  });

  const handleChangeWrapper = (e) => {
    setEdit(true);
    handleChange(e);
  }

    const handleFeaturesChange = (event, newValue) => {
        setEdit(true);
        setFieldValue('enabledFeatures', newValue);
    };

    return (
      <Box>
        <form>
          <Label value='Redirect Url' tooltip={Tooltips.redirectUrl}/>
          <TextField
            id='redirectUrl'
            name='redirectUrl'
            value={values.redirectUrl}
            onChange={handleChangeWrapper}
            helperText={errors.redirectUrl}
            error={errors.redirectUrl}
            sx={{ width: '50%', minWidth: 380 }}
          /> <br /> <br />

          <Label value='Configure Pages and Features' tooltip={Tooltips.enabledFeatures}/>
          <MUIAutocomplete
            multiple
            id='enabledFeatures'
            options={FEATURE_OPTIONS}
            disableCloseOnSelect
            value={values.enabledFeatures}
            onChange={handleFeaturesChange}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            sx={{
              width: '50%',
              minWidth: 380,
              '& .MuiOutlinedInput-root': {
                paddingRight: '9px !important',
              },
              '& .MuiAutocomplete-popupIndicator': {
                color: 'primary.main',
              },
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.label}
              </li>
            )}
            renderInput={(params) => (
              <MUITextField
                {...params}
                placeholder="Select features"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  readOnly: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-input': {
                    cursor: 'pointer',
                  },
                }}
              />
            )}
          />

          <br /> <br />

          {(edit) && <Box className={classes.buttonContainer}>
            <ActionButtons
              loading={loading}
              primaryFirst
              primaryAction="Save"
              secondaryAction="Cancel"
              primaryActionVariant="primary"
              secondaryActionVariant="error"
              onSecondaryClick={() => setEdit(false)}
              onPrimaryClick={handleSubmit}
            />
          </Box>}

          <br /> <br />
        </form>

        <Divider />
        <br />

        <Typography variant="h6-medium">
          Header Logo
        </Typography>
        <InprepLogoEditor enabledFeatures={values.enabledFeatures} />
      </Box>
    );
}